import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

type StorageProviderOption = "local" | "r2" | "gcs";

interface StorageSettingsFormProps {
	settings: any;
	onUpdated?: () => void;
}

export function StorageSettingsForm({ settings, onUpdated }: StorageSettingsFormProps) {
	const { t } = useTranslation();
	const [provider, setProvider] = useState<StorageProviderOption>("local");
	const [r2Form, setR2Form] = useState({
		accessKeyId: "",
		secretAccessKey: "",
		endpoint: "",
		bucketName: "",
	});
	const [gcsForm, setGcsForm] = useState({
		projectId: "",
		bucketName: "",
		credentialsJson: "",
	});

	const updateR2Mutation = trpc.settings.updateR2.useMutation({
		onSuccess: () => {
			toast.success(t.settings.settingsSaved || "Speichereinstellungen aktualisiert");
			onUpdated?.();
		},
		onError: (error) => toast.error(error.message || "Speichern fehlgeschlagen"),
	});

	const updateGcsMutation = trpc.settings.updateGCS.useMutation({
		onSuccess: () => {
			toast.success(t.settings.settingsSaved || "Speichereinstellungen aktualisiert");
			onUpdated?.();
		},
		onError: (error) => toast.error(error.message || "Speichern fehlgeschlagen"),
	});

	const useLocalStorageMutation = trpc.settings.useLocalStorage.useMutation({
		onSuccess: () => {
			toast.success(t.settings.settingsSaved || "Speichereinstellungen aktualisiert");
			onUpdated?.();
		},
		onError: (error) => toast.error(error.message || "Speichern fehlgeschlagen"),
	});

	const testR2Mutation = trpc.settings.testR2Connectivity.useMutation({
		onSuccess: (result) => {
			if (result.success) {
				toast.success(
					t.settings.r2TestSuccess
						? `${t.settings.r2TestSuccess} (${result.latencyMs}ms)`
						: `R2-Verbindung erfolgreich (${result.latencyMs}ms)`
				);
			} else {
				toast.error(result.error || t.settings.r2TestFailed || "Verbindung fehlgeschlagen");
			}
		},
		onError: (error) => toast.error(error.message || "Verbindung fehlgeschlagen"),
	});

	useEffect(() => {
		if (!settings) return;
		setProvider((settings.storageProvider as StorageProviderOption) || "local");
		setR2Form({
			accessKeyId: settings.r2AccessKeyId || "",
			secretAccessKey: settings.r2SecretAccessKey || "",
			endpoint: settings.r2Endpoint || "",
			bucketName: settings.r2BucketName || "",
		});
		setGcsForm({
			projectId: settings.gcsProjectId || "",
			bucketName: settings.gcsBucketName || "",
			credentialsJson: settings.gcsCredentialsJson || "",
		});
	}, [settings]);

	const isSaving = updateR2Mutation.isPending || updateGcsMutation.isPending || useLocalStorageMutation.isPending;

	const canSave = useMemo(() => {
		if (provider === "local") return true;
		if (provider === "r2") {
			return Boolean(r2Form.accessKeyId && r2Form.secretAccessKey && r2Form.endpoint && r2Form.bucketName);
		}
		if (provider === "gcs") {
			return Boolean(gcsForm.bucketName && gcsForm.credentialsJson);
		}
		return false;
	}, [provider, r2Form, gcsForm]);

	const handleSave = () => {
		if (provider === "local") {
			useLocalStorageMutation.mutate();
			return;
		}

		if (provider === "r2") {
			updateR2Mutation.mutate({
				r2AccessKeyId: r2Form.accessKeyId,
				r2SecretAccessKey: r2Form.secretAccessKey,
				r2Endpoint: r2Form.endpoint,
				r2BucketName: r2Form.bucketName,
			});
			return;
		}

		if (provider === "gcs") {
			updateGcsMutation.mutate({
				gcsProjectId: gcsForm.projectId,
				gcsBucketName: gcsForm.bucketName,
				gcsCredentialsJson: gcsForm.credentialsJson,
			});
		}
	};

	if (!settings) {
		return (
			<Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
				<CardHeader>
					<CardTitle>{t.settings.storageProviderTitle || "Speicher-Anbieter"}</CardTitle>
					<CardDescription>{t.common.loading || "Lädt..."}</CardDescription>
				</CardHeader>
				<CardContent>
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					{t.settings.storageProviderTitle || "Speicher-Anbieter"}
				</CardTitle>
				<CardDescription>
					{t.settings.storageProviderDescription || "Wählen Sie, wo Ihre Bilder gespeichert werden"}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-6">
				<div className="space-y-2">
					<Label>{t.settings.storageProviderLabel || "Anbieter"}</Label>
					  <Select value={provider} onValueChange={(value) => setProvider(value as StorageProviderOption)}>
						<SelectTrigger>
							<SelectValue placeholder={t.settings.storageProviderPlaceholder || "Speicher wählen"} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="local">{t.settings.storageOptionLocal || "Lokal (Server)"}</SelectItem>
							<SelectItem value="r2">{t.settings.storageOptionR2 || "Cloudflare R2"}</SelectItem>
							<SelectItem value="gcs">{t.settings.storageOptionGcs || "Google Cloud Storage"}</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-sm text-muted-foreground">
						{t.settings.storageProviderHelp || "Cloudspeicher vermeidet Datenverlust bei Deployments."}
					</p>
				</div>

				{provider === "local" && (
					<div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/20 p-4 text-sm text-yellow-900 dark:text-yellow-200 flex items-start gap-3">
						<AlertTriangle className="h-5 w-5 mt-0.5" />
						<div>
							<p className="font-semibold">{t.settings.localStorageWarningTitle || "Nur für Tests empfohlen"}</p>
							<p>{t.settings.localStorageWarning || "Uploads gehen bei jedem Deployment verloren. Verwenden Sie nach Möglichkeit R2 oder GCS."}</p>
						</div>
					</div>
				)}

				{provider === "r2" && (
					<div className="space-y-4">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label>Access Key ID</Label>
								<Input
									value={r2Form.accessKeyId}
									onChange={(e) => setR2Form((prev) => ({ ...prev, accessKeyId: e.target.value }))}
									placeholder="ABCD1234"
								/>
							</div>
							<div className="space-y-2">
								<Label>Secret Access Key</Label>
								<Input
									type="password"
									value={r2Form.secretAccessKey}
									onChange={(e) => setR2Form((prev) => ({ ...prev, secretAccessKey: e.target.value }))}
									placeholder="••••••••"
								/>
							</div>
						</div>
						<div className="grid gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label>{t.settings.r2Endpoint || "R2 Endpoint"}</Label>
								<Input
									value={r2Form.endpoint}
									onChange={(e) => setR2Form((prev) => ({ ...prev, endpoint: e.target.value }))}
									placeholder="xxxx.r2.cloudflarestorage.com"
								/>
							</div>
							<div className="space-y-2">
								<Label>{t.settings.r2BucketName || "Bucket Name"}</Label>
								<Input
									value={r2Form.bucketName}
									onChange={(e) => setR2Form((prev) => ({ ...prev, bucketName: e.target.value }))}
									placeholder="okulus-prod"
								/>
							</div>
						</div>
						<div className="flex flex-wrap gap-3">
							<Button onClick={handleSave} disabled={!canSave || isSaving}>
								{isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t.common.save}
							</Button>
							<Button
								type="button"
								variant="outline"
								onClick={() => testR2Mutation.mutate()}
								disabled={testR2Mutation.isPending}
								className="gap-2"
							>
								{testR2Mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
								{t.settings.r2TestConnection || "Verbindung testen"}
							</Button>
							{testR2Mutation.data?.success && (
								<span className="inline-flex items-center text-sm text-green-600 gap-1">
									<CheckCircle2 className="h-4 w-4" />
									{t.settings.r2TestSuccess || "R2 Verbindung erfolgreich"}
								</span>
							)}
						</div>
					</div>
				)}

				{provider === "gcs" && (
					<div className="space-y-4">
						<div className="space-y-2">
							<Label>{t.settings.gcsProjectId || "Projekt-ID"}</Label>
							<Input
								value={gcsForm.projectId}
								onChange={(e) => setGcsForm((prev) => ({ ...prev, projectId: e.target.value }))}
								placeholder="okulus-prod"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t.settings.gcsBucketName || "Bucket"}</Label>
							<Input
								value={gcsForm.bucketName}
								onChange={(e) => setGcsForm((prev) => ({ ...prev, bucketName: e.target.value }))}
								placeholder="okulus-images"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t.settings.gcsCredentialsJson || "Service Account JSON"}</Label>
																									<Textarea
								value={gcsForm.credentialsJson}
								onChange={(e) => setGcsForm((prev) => ({ ...prev, credentialsJson: e.target.value }))}
								rows={5}
																										placeholder={'{ "type": "service_account", ... }'}
							/>
						</div>
						<Button onClick={handleSave} disabled={!canSave || isSaving}>
							{isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t.common.save}
						</Button>
					</div>
				)}

				{provider === "local" && (
					<Button onClick={handleSave} disabled={isSaving}>
						{isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t.settings.useLocalStorage || "Lokal verwenden"}
					</Button>
				)}
			</CardContent>
		</Card>
	);
}
